import BaseEntityService from './BaseEntityService';
import axiosInstance from '../../services/api';

/**
 * Product API Service extending BaseEntityService
 */
export class ProductService extends BaseEntityService {
  static baseUrl = '/api/commercial-products';

  /**
   * Filter Products with parameters
   * @param {Object} [params={}] - Filter parameters
   * @returns {Promise<Object>} Filtered products with pagination
   */
  static async filterProduct(params = {}) {
    return super.filterEntities(this.baseUrl, params);
  }

  /**
   * Retrieves a Product by ID or name
   * @param {string|number} identifier - UUID or name of the Product
   * @returns {Promise<Object>} The Product data
   */
  static async getProduct(identifier) {
    return super.getEntity(
      this.baseUrl, 
      identifier, 
      '?include_products=true&include_health=true&include_environment=true&include_treatment_enemies=true&include_treatment_cultures=true'
    );
  }

  /**
   * Fetches culture tree for an Product
   * @param {string} productId - Product ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Tree data response
   */
  static async getCultureTree(productId, params = {}) {
    try {
      const { data } = await axiosInstance.get(
        `/api/cultures/products/${productId}/culture-tree/`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching culture tree:', error);
      throw error;
    }
  }

  /**
   * Fetches hierarchical culture tree for a Product with parent-child relationships
   * @param {string} productId - Product ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Hierarchical tree data response
   */
  static async getProductCultureTree(productId, params = {}) {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseUrl}/${productId}/culture-tree/`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching product culture tree:', error);
      throw error;
    }
  }
}

/**
 * Query keys factory for React Query cache management
 * Provides consistent keys for caching and invalidation
 */
export const productKeys = {
  all: ['products'],
  filters: () => [...productKeys.all, 'filter'],
  filter: (params) => [...productKeys.filters(), params],
  details: () => [...productKeys.all, 'detail'],
  detail: (id) => [...productKeys.details(), id],
  cultureTree: (id, params) => [
    ...productKeys.detail(id),
    'cultureTree',
    params,
  ],
};
